import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT, S3_BASE_URL } from "src/constants/endpoints";
import { GET_MENU_CATEGORIES, GET_ITEMS } from "src/constants/queries";
import {
  set_menu_items,
  search_menu_items,
  delete_menu_item_modal,
} from "src/store/features/menu-item-slice";
import { RootState } from "src/store";
import Spinner from "src/components/common/Spinner";
import MenuItemCard from "src/features/MenuItems/components/MenuItemCard";
import ManageMenuItemModal from "src/features/MenuItems/components/ManageMenuItemModal";
import MenuCategoryModal from "src/features/MenuCategories/components/MenuCategoryModal";
import DeleteCategory from "src/features/MenuCategories/components/DeleteCategory";
import type { MenuCategory, MenuItem } from "src/interfaces/menu.interface";
import type { UUID } from "src/types/shared.type";
import RestuarantImg from "src/assests/images/dashboard/default-thumbnail (1).jpg";
import useOutsideClick from "src/hooks/useOutsideClick";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { restaurantSegment } from "src/utils/restaurantUrl";
import { useAuth } from "src/contexts/auth-provider";

type CategoryItemsActiveFilter = "ACTIVE" | "INACTIVE" | "ALL";

const CATEGORY_ITEMS_FILTER_TABS: { id: CategoryItemsActiveFilter; label: string }[] = [
  { id: "ACTIVE", label: "Active" },
  { id: "INACTIVE", label: "Inactive" },
  { id: "ALL", label: "All" },
];

export default function CategoryDetailPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { hasRestaurant } = useAuth();
  const { restaurantCorrelationId, redirectTo } = useRestaurantFromParams();
  const segment = restaurantCorrelationId ? restaurantSegment(restaurantCorrelationId, hasRestaurant?.name) : "";
  const navigate = useNavigate();
  const { axBe } = useAxiosInterceptor();
  const dispatch = useDispatch();

  const [category, setCategory] = useState<MenuCategory | null>(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [menuItemToEdit, setMenuItemToEdit] = useState<MenuItem | null>(null);
  const [isShowMenuItemModal, setIsShowMenuItemModal] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const itemSearchQueryRef = useRef("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [itemsActiveFilter, setItemsActiveFilter] = useState<CategoryItemsActiveFilter>("ACTIVE");
  useOutsideClick({ ref: overflowRef, handler: () => setOverflowOpen(false) });

  const { filteredMenuItems } = useSelector((state: RootState) => state.menuItem);

  const fetchCategory = useCallback(async () => {
    if (!restaurantCorrelationId || !categoryId) return;
    setCategoryLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: { storeCorrelationId: restaurantCorrelationId, activeFilter: "ALL" },
      });
      const categories: MenuCategory[] = data?.data?.categoriesByStoreCorrelationId ?? [];
      const found = categories.find((c: MenuCategory) => c.correlationId === categoryId);
      setCategory(found ?? null);
    } catch {
      setCategory(null);
    } finally {
      setCategoryLoading(false);
    }
  }, [restaurantCorrelationId, categoryId, axBe]);

  const getMenuItems = useCallback(async () => {
    if (!categoryId) return;
    setItemsLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_ITEMS,
        variables: { categoryCorrelationId: categoryId, activeFilter: itemsActiveFilter },
      });
      dispatch(
        set_menu_items({
          categoryItems: data?.data?.itemsByCategoryCorrelationId ?? [],
        })
      );
      dispatch(search_menu_items(itemSearchQueryRef.current));
    } catch (error) {
      console.error(error);
    } finally {
      setItemsLoading(false);
    }
  }, [categoryId, axBe, dispatch, itemsActiveFilter]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  useEffect(() => {
    itemSearchQueryRef.current = "";
    setItemSearchQuery("");
    dispatch(search_menu_items(""));
  }, [categoryId, dispatch]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);

  const searchMenuCategoryItems = (query: string) => {
    itemSearchQueryRef.current = query;
    setItemSearchQuery(query);
    dispatch(search_menu_items(query));
  };

  const handleMenuItemClick = (menuItemId: UUID) => {
    dispatch(delete_menu_item_modal(menuItemId));
  };

  const handleCreateMenuItem = () => {
    setMenuItemToEdit(null);
    setIsShowMenuItemModal(true);
  };

  const handleEditMenuItem = (_e: React.MouseEvent<HTMLElement>, menuItem: MenuItem) => {
    setMenuItemToEdit(menuItem);
    setIsShowMenuItemModal(true);
  };

  const handleCloseItemModal = () => {
    setMenuItemToEdit(null);
    setIsShowMenuItemModal(false);
    getMenuItems();
  };

  const handleAfterDelete = () => {
    setShowDeleteModal(false);
    navigate(`/restaurants/${segment}/menu`, { replace: true });
  };

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (!restaurantCorrelationId || !categoryId) {
    return (
      <div className="text-sm text-gray-500">
        <Link to={segment ? `/restaurants/${segment}/menu` : "/my-restaurants"} className="text-[#FD7657] hover:underline">
          ← Back to Categories
        </Link>
      </div>
    );
  }

  if (categoryLoading && !category) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!category) {
    return (
      <div>
        <p className="text-gray-500 text-sm">Category not found.</p>
        <Link
          to={`/restaurants/${segment}/menu`}
          className="text-[#FD7657] text-sm font-medium hover:underline mt-2 inline-block"
        >
          ← Back to Categories
        </Link>
      </div>
    );
  }

  const imageSrc = category.bucketKeyName
    ? category.bucketKeyName.includes("blob")
      ? category.bucketKeyName
      : S3_BASE_URL + category.bucketKeyName
    : RestuarantImg;

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-col pb-4">
      <p className="text-[9px] text-[#B0B8C0] mb-0.5" aria-hidden="true">
        <Link to={`/restaurants/${segment}/menu`} className="hover:text-[#FD7657] text-[#A0A8B0]">
          Categories
        </Link>
        {" / "}
        <span className="text-[#B0B8C0]">{category.name}</span>
      </p>

      <div className="flex flex-row items-center justify-between gap-3 pb-2 mb-2 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F3F4F6] shrink-0 flex-shrink-0">
            <img src={imageSrc} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[20px] font-semibold text-[#111] truncate leading-tight" style={{ fontWeight: 600 }}>{category.name}</h1>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              {category.items?.length ?? 0} {category.items?.length === 1 ? "item" : "items"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="h-8 px-3 rounded-lg text-sm font-medium border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
          >
            Edit Category
          </button>
          <div className="relative" ref={overflowRef}>
            <button
              type="button"
              className="h-8 w-8 rounded-lg border border-[#E5E7EB] bg-white flex items-center justify-center text-[#6B7280] hover:bg-[#F9FAFB]"
              aria-label="More actions"
              onClick={() => setOverflowOpen((o) => !o)}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <circle cx="10" cy="4" r="1.5" />
                <circle cx="10" cy="10" r="1.5" />
                <circle cx="10" cy="16" r="1.5" />
              </svg>
            </button>
            {overflowOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-10 min-w-[120px] py-1 bg-white rounded-lg border border-[#E5E7EB] shadow-lg"
                role="menu"
              >
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  role="menuitem"
                  onClick={() => {
                    setOverflowOpen(false);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 mb-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <form className="w-full min-w-0 sm:flex-1 sm:max-w-[260px]">
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg className="w-4 h-4 text-[#9CA3AF]" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
              </svg>
            </div>
            <input
              type="search"
              className="block border border-[#E5E7EB] w-full py-2 ps-10 pe-3 text-sm text-[#111827] rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#E5E7EB]"
              placeholder="Search Item"
              value={itemSearchQuery}
              onChange={(e) => searchMenuCategoryItems(e.target.value)}
              aria-label="Search items"
            />
          </div>
        </form>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end sm:shrink-0">
          <div
            className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
            role="group"
            aria-label="Filter items by status"
          >
            {CATEGORY_ITEMS_FILTER_TABS.map(({ id, label }) => {
              const selected = itemsActiveFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setItemsActiveFilter(id)}
                  className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
                    selected
                      ? "bg-[#FD7657] text-white shadow-sm"
                      : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={handleCreateMenuItem}
            className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] shrink-0 transition-colors w-full sm:w-auto"
          >
            Add Item
          </button>
        </div>
      </div>

      <div className="w-full min-w-0">
        {itemsLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : filteredMenuItems?.length > 0 ? (
          <div className="flex flex-wrap gap-y-4 gap-x-6">
            {filteredMenuItems.map((menuItem: MenuItem) => (
              <MenuItemCard
                key={menuItem.correlationId}
                menuItem={menuItem}
                handleMenuItemClick={handleMenuItemClick}
                onClick={handleEditMenuItem}
              />
            ))}
          </div>
        ) : (
          <p className="text-[#6b7280] py-12 text-center text-sm">
            No items. Click &quot;Add Item&quot; to create one.
          </p>
        )}
      </div>

      <ManageMenuItemModal
        key={
          isShowMenuItemModal
            ? menuItemToEdit?.correlationId ?? "new-item"
            : "closed"
        }
        isOpen={isShowMenuItemModal}
        onClose={handleCloseItemModal}
        menuCategoryId={categoryId}
        menuCategoryName={category.name}
        menuItemToEdit={menuItemToEdit}
        getMenuItems={getMenuItems}
      />

      {showEditModal && (
        <MenuCategoryModal
          menuCategoryTitle="Edit Category"
          setShowCreateModal={() => {
            setShowEditModal(false);
            fetchCategory();
          }}
          editMenuCategory={category}
        />
      )}

      {showDeleteModal && (
        <DeleteCategory
          categoryId={categoryId}
          onClose={() => setShowDeleteModal(false)}
          onAfterDelete={handleAfterDelete}
        />
      )}
    </div>
  );
}
